import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMakeDays, getMakeDay, getSuppliers, getSilos, getMilkIntake } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Eye, Loader2, FlaskConical, Droplets, PackageOpen, Boxes, Plus, Edit } from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-zinc-200 text-zinc-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  locked: 'bg-blue-100 text-blue-700',
};

export default function MakeDays() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [silos, setSilos] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);

  useEffect(() => { (async () => {
    setLoading(true);
    try {
      const [m, s, sl, mi] = await Promise.all([getMakeDays(), getSuppliers(), getSilos(), getMilkIntake()]);
      setItems(m.data); setSuppliers(s.data); setSilos(sl.data); setIntakes(mi.data);
    } catch (e) { toast.error('Failed to load make days'); }
    finally { setLoading(false); }
  })(); }, []);

  const supName = (id) => suppliers.find(s => s.id === id)?.name || '—';
  const siloName = (id) => silos.find(s => s.id === id)?.name || '—';
  const intakeRef = (id) => intakes.find(s => s.id === id)?.intake_ref || '—';

  const filtered = useMemo(() => items.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.make_day_ref || '').toLowerCase().includes(q)
      || (d.product_type || '').toLowerCase().includes(q)
      || (d.supervisor || '').toLowerCase().includes(q);
  }), [items, search]);

  const openView = async (d) => { try { const r = await getMakeDay(d.id); setViewing(r.data); } catch (e) { toast.error('Load failed'); } };

  return (
    <div className="space-y-4" data-testid="make-days-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><FlaskConical className="w-5 h-5" />Make Days</h1>
          <p className="text-sm text-muted-foreground">Production · the bridge between inward goods and outward cheese batches. Each make day pins milk, ingredients and packaging to specific batches.</p>
        </div>
        <Button onClick={() => navigate('/production/make-days/new')} data-testid="btn-new-makeday"><Plus className="w-4 h-4 mr-1" />New Make Day</Button>
      </div>

      <Card><CardContent className="pt-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search make-day ref, product, supervisor…" className="pl-9" />
        </div>
      </CardContent></Card>

      {loading ? <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> :
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr><th className="text-left px-4 py-3">Make Day</th><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Product</th><th className="text-left px-4 py-3">Supervisor</th><th className="text-left px-4 py-3">Inputs</th><th className="text-left px-4 py-3">Outputs</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">No make days yet.</td></tr> :
                filtered.map(d => {
                  const milkL = (d.milk_used || []).reduce((s, m) => s + (m.quantity_litres || 0), 0);
                  return (
                    <tr key={d.id} className="border-t hover:bg-zinc-50" data-testid={`make-day-row-${d.id}`}>
                      <td className="px-4 py-3 font-mono text-xs">{d.make_day_ref}</td>
                      <td className="px-4 py-3">{d.make_date}</td>
                      <td className="px-4 py-3 font-medium">{d.product_type || '—'}</td>
                      <td className="px-4 py-3 text-sm">{d.supervisor || '—'}</td>
                      <td className="px-4 py-3 text-xs">{milkL.toLocaleString()}L · {(d.ingredients_used || []).length}ing · {(d.packaging_used || []).length}pkg</td>
                      <td className="px-4 py-3 text-xs">{(d.output_batches || []).length} batch{(d.output_batches || []).length !== 1 ? 'es' : ''}</td>
                      <td className="px-4 py-3"><Badge className={`${STATUS_COLORS[d.status] || ''} border-0`}>{d.status}</Badge></td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openView(d)}><Eye className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/production/make-days/${d.id}/edit`)} data-testid={`btn-edit-md-${d.id}`}><Edit className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </CardContent></Card>}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-auto">
          {viewing && <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-mono">{viewing.make_day_ref}
                <Badge className={`${STATUS_COLORS[viewing.status] || ''} border-0`}>{viewing.status}</Badge>
              </DialogTitle>
              <DialogDescription>{viewing.product_type} · {viewing.make_date} · {viewing.production_site || '—'} · Supervisor: {viewing.supervisor || '—'}</DialogDescription>
            </DialogHeader>

            <section className="mt-2">
              <h3 className="text-sm font-semibold flex items-center gap-2 mb-2"><Droplets className="w-4 h-4" />Milk used</h3>
              <table className="w-full text-sm border rounded">
                <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr><th className="text-left px-3 py-2">Silo</th><th className="text-left px-3 py-2">Linked intake</th><th className="text-right px-3 py-2">Litres</th></tr></thead>
                <tbody>{(viewing.milk_used || []).map((m, i) => (<tr key={i} className="border-t">
                  <td className="px-3 py-2">{siloName(m.silo_id)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{intakeRef(m.milk_intake_id)}</td>
                  <td className="px-3 py-2 text-right font-medium">{m.quantity_litres?.toLocaleString()}L</td>
                </tr>))}</tbody>
              </table>
            </section>

            <section className="mt-4">
              <h3 className="text-sm font-semibold flex items-center gap-2 mb-2"><PackageOpen className="w-4 h-4" />Ingredients used</h3>
              <table className="w-full text-sm border rounded">
                <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Supplier</th><th className="text-left px-3 py-2">Lot</th><th className="text-right px-3 py-2">Qty</th></tr></thead>
                <tbody>{(viewing.ingredients_used || []).map((it, i) => (<tr key={i} className="border-t">
                  <td className="px-3 py-2">{it.internal_item}<div className="text-xs text-muted-foreground">{it.supplier_product_code}</div></td>
                  <td className="px-3 py-2">{supName(it.supplier_id)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{it.supplier_lot || '—'}</td>
                  <td className="px-3 py-2 text-right">{it.quantity} {it.unit}</td>
                </tr>))}</tbody>
              </table>
            </section>

            <section className="mt-4">
              <h3 className="text-sm font-semibold flex items-center gap-2 mb-2"><PackageOpen className="w-4 h-4" />Packaging used</h3>
              <table className="w-full text-sm border rounded">
                <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Supplier</th><th className="text-left px-3 py-2">Lot</th><th className="text-right px-3 py-2">Qty</th></tr></thead>
                <tbody>{(viewing.packaging_used || []).map((it, i) => (<tr key={i} className="border-t">
                  <td className="px-3 py-2">{it.internal_item}<div className="text-xs text-muted-foreground">{it.supplier_product_code}</div></td>
                  <td className="px-3 py-2">{supName(it.supplier_id)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{it.supplier_lot || '—'}</td>
                  <td className="px-3 py-2 text-right">{it.quantity} {it.unit}</td>
                </tr>))}</tbody>
              </table>
            </section>

            <section className="mt-4">
              <h3 className="text-sm font-semibold flex items-center gap-2 mb-2"><Boxes className="w-4 h-4" />Output batches</h3>
              <div className="flex gap-2 flex-wrap">
                {(viewing.output_batches || []).map((b, i) => (
                  <div key={i} className="border rounded px-3 py-2 bg-emerald-50 border-emerald-200">
                    <div className="font-mono text-sm font-semibold">{b.batch_number}</div>
                    <div className="text-xs text-muted-foreground">{b.product} · {b.quantity}{b.unit}</div>
                  </div>
                ))}
              </div>
            </section>

            <DialogFooter><Button variant="outline" onClick={() => setViewing(null)}>Close</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
